import React from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { useFormContext, Controller } from "react-hook-form";
import { PhoneNumberUtil } from "google-libphonenumber";
import TailwindPhoneInput from "./TailwindPhoneInput";

const phoneUtil = PhoneNumberUtil.getInstance();

const isPhoneValid = (phone) => {
  if (!phone) return false;
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone));
  } catch {
    return false;
  }
};

const labelStyle = "block text-sm font-medium text-gray-300 mb-2";
const inputStyle =
  "w-full px-4 py-3 rounded-lg border border-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200 bg-gray-800 text-gray-200";

function FormStep2({ copy, showErrors }) {
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <div className="space-y-6">
      <div>
        <label htmlFor="country" className={labelStyle}>
          {copy.fieldLabels.country}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <motion.input
          type="text"
          id="country"
          {...register("country", { required: copy.errorMessages.required })}
          className={`${inputStyle} ${showErrors && errors.country ? "border-red-500" : ""}`}
          placeholder={copy.placeholders.country}
          whileFocus={{ scale: 1.01 }}
        />
        {showErrors && errors.country && (
          <p className="mt-1 text-sm text-red-500">{errors.country.message}</p>
        )}
      </div>
      <div>
        <label htmlFor="phoneNumber" className={labelStyle}>
          {copy.fieldLabels.phoneNumber}
          <span className="text-red-500 ml-1">*</span>
        </label>
        <Controller
          name="phoneNumber"
          control={control}
          rules={{
            required: copy.errorMessages.required,
            validate: (value) =>
              isPhoneValid(value) ||
              copy.errorMessages.invalidPhone ||
              "Invalid phone number",
          }}
          render={({ field }) => (
            <TailwindPhoneInput
              defaultCountry="bg"
              value={field.value}
              onChange={field.onChange}
              placeholder={copy.placeholders.phoneNumber}
              error={showErrors && errors.phoneNumber}
            />
          )}
        />
        {showErrors && errors.phoneNumber && (
          <p className="mt-1 text-sm text-red-500">
            {errors.phoneNumber.message}
          </p>
        )}
      </div>
    </div>
  );
}

FormStep2.propTypes = {
  copy: PropTypes.shape({
    fieldLabels: PropTypes.shape({
      country: PropTypes.string.isRequired,
      phoneNumber: PropTypes.string.isRequired,
    }).isRequired,
    errorMessages: PropTypes.shape({
      required: PropTypes.string.isRequired,
      invalidPhone: PropTypes.string.isRequired,
    }).isRequired,
    placeholders: PropTypes.shape({
      country: PropTypes.string.isRequired,
      phoneNumber: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  showErrors: PropTypes.bool.isRequired,
};

export default React.memo(FormStep2);
